import { resolveColumnWidth } from '../column/meta'
import type { GridColumnInfo } from '../types'

export type GridFixedType = '' | 'left' | 'right'

export interface GridColumnLayout {
  leftList: GridColumnInfo[]
  centerList: GridColumnInfo[]
  rightList: GridColumnInfo[]
  orderedColumns: GridColumnInfo[]
}

export function splitColumnsByFixed(columns: GridColumnInfo[]): GridColumnLayout {
  const leftList: GridColumnInfo[] = []
  const centerList: GridColumnInfo[] = []
  const rightList: GridColumnInfo[] = []

  for (const column of columns) {
    if (column.fixed === 'left') {
      leftList.push(column)
    } else if (column.fixed === 'right') {
      rightList.push(column)
    } else {
      centerList.push(column)
    }
  }

  return {
    leftList,
    centerList,
    rightList,
    orderedColumns: [...leftList, ...centerList, ...rightList],
  }
}

/** 与 ZTable 一致：左固定 + 中间 + 右固定，便于固定层裁剪对齐 */
export function orderColumnsByFixed(columns: GridColumnInfo[]) {
  return splitColumnsByFixed(columns).orderedColumns
}

export function resolveTableWidth(columns: GridColumnInfo[]) {
  return columns.reduce((sum, col) => sum + resolveColumnWidth(col), 0)
}

/** 左固定层：仅包含显式设置 fixed:left 的列 */
export function resolveLeftPanelColumns(columns: GridColumnInfo[]) {
  return splitColumnsByFixed(columns).leftList
}

/** 右固定层：仅包含显式设置 fixed:right 的列 */
export function resolveRightPanelColumns(columns: GridColumnInfo[]) {
  return splitColumnsByFixed(columns).rightList
}

/** 固定层渲染列：与主表 layoutColumns 同序，仅保留 panel 内列 */
export function filterPanelColumnsByLayout(
  layoutColumns: GridColumnInfo[],
  panelColumns: GridColumnInfo[]
) {
  if (!panelColumns.length) {
    return []
  }
  const panelIds = new Set(panelColumns.map((col) => col.id))
  return layoutColumns.filter((col) => panelIds.has(col.id))
}

function resolvePanelColumnIds(columns: GridColumnInfo[], side: 'left' | 'right') {
  const panel =
    side === 'left' ? resolveLeftPanelColumns(columns) : resolveRightPanelColumns(columns)
  return new Set(panel.map((col) => col.id))
}

export function isFixedColumnHidden(
  column: GridColumnInfo,
  fixedType: GridFixedType,
  overflowX: boolean,
  sourceColumns?: GridColumnInfo[]
) {
  if (!overflowX) {
    return false
  }
  const columns = sourceColumns ?? []
  const leftPanelIds = columns.length ? resolvePanelColumnIds(columns, 'left') : null
  const rightPanelIds = columns.length ? resolvePanelColumnIds(columns, 'right') : null

  if (fixedType === 'left') {
    if (leftPanelIds) {
      return !leftPanelIds.has(column.id)
    }
    return column.fixed !== 'left'
  }
  if (fixedType === 'right') {
    if (rightPanelIds) {
      return !rightPanelIds.has(column.id)
    }
    return column.fixed !== 'right'
  }
  if (leftPanelIds?.has(column.id) || rightPanelIds?.has(column.id)) {
    return true
  }
  return !!column.fixed
}

export function computeFixedWidth(
  columns: GridColumnInfo[],
  side: 'left' | 'right'
) {
  const panel =
    side === 'left' ? resolveLeftPanelColumns(columns) : resolveRightPanelColumns(columns)
  return panel.reduce((sum, col) => sum + resolveColumnWidth(col), 0)
}

/** 首个左固定列之前、非左固定列的累计宽度（自然列序） */
export function resolveLeftFixedLeadingOffset(columns: GridColumnInfo[]) {
  let offset = 0
  for (const column of columns) {
    if (column.fixed === 'left') {
      break
    }
    offset += resolveColumnWidth(column)
  }
  return offset
}

/** 左固定层 left 偏移：未滚动时对齐自然位置，滚动后贴齐视口左侧 */
export function resolveLeftFixedPanelOffset(
  leadingOffset: number,
  scrollLeft: number
) {
  return Math.max(0, leadingOffset - scrollLeft)
}

/** 左固定列已贴齐视口左侧（可展示滚动阴影） */
export function isLeftFixedStuck(leadingOffset: number, scrollLeft: number) {
  return scrollLeft > 0 && scrollLeft >= leadingOffset
}

export function hasFixedColumns(columns: GridColumnInfo[]) {
  return columns.some((col) => col.fixed === 'left' || col.fixed === 'right')
}
